import { mergeDailyTrends, zoneForCity } from './cityZones';
import { campaignGroupCityTags } from '../../services/metaAdsService';
import { dailySeries } from '../../services/googleAdsService';

function cityStorageKey(cityName) {
    if (cityName === 'Untagged') return '__untagged';
    return cityName;
}

export function unifiedCityTags(campaign) {
    if (campaign?.channel === 'meta') {
        const tags = campaignGroupCityTags(campaign);
        return tags.length ? tags : ['Untagged'];
    }
    const cities = campaign?.cities?.length ? campaign.cities : [];
    return cities.length ? cities : ['Untagged'];
}

export function combinedCampaignInScope(campaign, { city, zones }) {
    const tags = unifiedCityTags(campaign);
    if (city === '__untagged') {
        return tags.includes('Untagged');
    }
    if (city !== 'all') {
        return tags.includes(city);
    }
    if (zones?.length) {
        return tags.some((tag) => tag !== 'Untagged' && zones.includes(zoneForCity(tag)));
    }
    return true;
}

export function combinedKnownCities(unifiedCampaigns, masterCities = []) {
    const names = new Set(masterCities);
    (unifiedCampaigns || []).forEach((campaign) => {
        unifiedCityTags(campaign).forEach((tag) => {
            if (tag && tag !== 'Untagged') names.add(tag);
        });
    });
    return [...names].sort((left, right) => left.localeCompare(right));
}

function cityKeysForScope(metaCityDaily, { city, zones }) {
    const store = metaCityDaily?.cities || {};
    const entries = Object.keys(store).map((key) => ({
        key,
        name: key === '__untagged' ? 'Untagged' : key,
    }));
    if (city === '__untagged') {
        return entries.filter(({ key }) => key === '__untagged').map(({ key }) => key);
    }
    if (city !== 'all') {
        return [cityStorageKey(city)];
    }
    if (zones?.length) {
        return entries
            .filter(({ name }) => name !== 'Untagged' && zones.includes(zoneForCity(name)))
            .map(({ key }) => key);
    }
    return entries.map(({ key }) => key);
}

export function metaScopeDaily(metaCityDaily, scope) {
    if (!metaCityDaily?.dates?.length || !metaCityDaily?.cities) return [];
    const keys = cityKeysForScope(metaCityDaily, scope);
    return metaCityDaily.dates.map((date) => {
        const row = {
            date,
            spend: 0,
            clicks: 0,
            impressions: 0,
            leadCount: 0,
            currency: 'INR',
        };
        keys.forEach((key) => {
            const day = metaCityDaily.cities[key]?.[date];
            if (!day) return;
            row.spend += day.spend || 0;
            row.clicks += day.clicks || 0;
            row.impressions += day.impressions || 0;
            row.leadCount += day.leadCount || 0;
            if (day.currency) row.currency = day.currency;
        });
        return row;
    });
}

export function googleRecordInScope(row, { city, zones }) {
    const tags = row?.cities?.length ? row.cities : [];
    if (city === '__untagged') return tags.length === 0;
    if (city !== 'all') return tags.includes(city);
    if (zones?.length) {
        return tags.some((name) => zones.includes(zoneForCity(name)));
    }
    return true;
}

export function combinedScopeDaily({
    metaCityDaily,
    records,
    visibleCampaigns,
    city,
    zones,
}) {
    const googleIds = new Set(
        (visibleCampaigns || [])
            .filter((campaign) => campaign.channel === 'google')
            .map((campaign) => String(campaign.reportingCampaignId || campaign.campaignId).replace(/^google:/, '')),
    );
    const scopedRecords = (records || []).filter((row) => {
        if (!googleIds.has(String(row.campaignId))) return false;
        return googleRecordInScope(row, { city, zones });
    });
    const metaDaily = metaScopeDaily(metaCityDaily, { city, zones });
    const googleDaily = dailySeries(scopedRecords);
    return mergeDailyTrends([metaDaily, googleDaily]);
}
